/* tslint:disable:no-duplicate-string */
import axios, { type AxiosInstance, AxiosError, AxiosResponse, AxiosRequestConfig, Method } from 'axios';
import { get, isNil } from 'lodash';
import { asyncScheduler, Observable, of, scheduled, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import {
	API_ENDPOINT_INFORMATION_RECORDS,
	ApiEndpointConfiguration,
	CONFIGURATION_SERVICE,
	HttpStatusCode,
	HttpStatusText,
	IApiEndpointInformation,
	IAuthTokenService,
	IConfigurationService,
	IConnectionState,
	isAuthGrantType,
	IServerApiBuilderService,
	MobizHttpErrorResponse,
	MobizHttpErrorResponseHelper,
	MobizServerApi,
	MobizSession,
	RequestBodyValue,
	SerializableObject,
	UnknownNetworkError,
} from '../../core';
import IConnectionHealthCheck from '../../core/services/connection-state.service';
import { Inject } from '../../injection';
import { SerializableParams } from '../../utils';

import { AuthGrantType } from '../../core/services/auth-token-grant';
import { AXIOS_STATIC_HTTP_CLIENT } from '../other/axios-static-http-client.factory';

/**
 * @internal
 */
export class ConnectionHelperService {
	private readonly CONNECTION_STATE_TIMEOUT: number = 10000;
	private readonly httpErrorResponseHelper: MobizHttpErrorResponseHelper;

	constructor(
		@Inject(API_ENDPOINT_INFORMATION_RECORDS)
		public readonly apiEndpointInformationRecords: Record<string, IApiEndpointInformation>[],
		@Inject(IAuthTokenService) private readonly authTokenService: IAuthTokenService,
	@Inject(AXIOS_STATIC_HTTP_CLIENT) private readonly axiosStatic: AxiosInstance,
		@Inject(CONFIGURATION_SERVICE) public readonly configurationService: IConfigurationService,
		@Inject(IServerApiBuilderService) private readonly serverApiBuilderService: IServerApiBuilderService
	) {
		this.httpErrorResponseHelper = new MobizHttpErrorResponseHelper();
	}

	/**
	 * Method used by CONNECTION_STATE_REFRESHER.
	 */
	public checkConnectionState(): Observable<IConnectionState> {
		return this.createRequestObservable('GET', {
			serverApi: MobizServerApi.HealthCheck,
		}).pipe(
			// Cancel the HTTP Request after 10 seconds.
			timeout(this.CONNECTION_STATE_TIMEOUT),
			map((responseContent: IConnectionHealthCheck | unknown): IConnectionState => {
				const healthy: boolean = get(responseContent, 'healthy', false);
				return { network: 'online', api: healthy ? 'online' : 'offline' };
			}),
			catchError((error: AxiosError): Observable<IConnectionState> => {
				const isNetworkError = this.isNetworkErrorResponse(error);
				return of({ network: isNetworkError ? 'offline' : 'online', api: 'unknown' });
			})
		);
	}

	public createRequestObservable<T extends SerializableObject | unknown>(
		requestMethod: Method,
		requestConfiguration: ApiEndpointConfiguration<string>,
		responseTypeInstance?: T
	): Observable<T> {
		const apiEndpointInformation = this.getApiEndpointInformation(requestConfiguration);
		const headers = this.prepareHeaders(
			apiEndpointInformation.grantType,
			requestConfiguration.headers,
			requestConfiguration.body
		);
		const body = this.prepareBody(requestConfiguration.body);
		const requestUrl = this.serverApiBuilderService.getUrl(
			requestConfiguration.serverApi,
			requestConfiguration.urlParameters,
			requestConfiguration.queryParameters
		);
		const config: AxiosRequestConfig = {
			method: requestMethod,
			url: requestUrl,
			data: body,
			headers: this.convertHeadersToRecord(headers),
			responseType: 'json',
			withCredentials: true,
			...(requestConfiguration.timeout && { timeout: requestConfiguration.timeout }),
		};
		return scheduled(this.axiosStatic(config), asyncScheduler).pipe(
			map((axiosResponse: AxiosResponse<unknown>): T => {
				if (responseTypeInstance instanceof SerializableObject) {
					responseTypeInstance.buildFromJSON(axiosResponse.data);
					if (responseTypeInstance instanceof MobizSession) {
						// The response doesn't include the Grant Type, which is required.
						responseTypeInstance.grantType = apiEndpointInformation.grantType;
					}
					return responseTypeInstance;
				}
				return axiosResponse.data as T;
			}),
			catchError((originalError: AxiosError | unknown): Observable<never> => {
				if (this.isAxiosError(originalError)) {
					// Convert `AxiosError` to `MobizHttpErrorResponse`.
					return this.handleHttpErrorResponse(requestConfiguration, originalError);
				}
				// Handle errors caused by the client side code deserialization and other things.
				return throwError(originalError);
			})
		);
	}

	private prepareBody(body: RequestBodyValue): RequestBodyValue {
		if (body instanceof SerializableObject || body instanceof SerializableParams) {
			return body.toString();
		}
		return body;
	}

	/**
	 * NOTE: For OAuth endpoints, the GrantType, identification headers are not included.
	 */
	public prepareHeaders(grantType?: AuthGrantType, headersInit?: HeadersInit, bodyValue?: RequestBodyValue): Headers {
		const headers = new Headers(headersInit);
		if (grantType) {
			this.authTokenService.getIdentificationHeaders(grantType).forEach((value: string, key: string): void => {
				if (!headers.has(key)) {
					headers.append(key, value);
				}
			});
		}
		if (!headers.has('Accept')) {
			headers.append('Accept', 'application/json');
		}
		if (!headers.has('Content-Type')) {
			if (bodyValue instanceof FormData) {
				// NOTE: The Content-Type header for FormData will be generated by the browser including the boundary information.
			} else if (bodyValue instanceof SerializableObject) {
				headers.append('Content-Type', 'application/json');
			} else if (bodyValue instanceof SerializableParams) {
				headers.append('Content-Type', 'application/x-www-form-urlencoded');
			} else if (typeof bodyValue === 'string' && bodyValue.length) {
				// Assume all strings are JSON strings.
				headers.append('Content-Type', 'application/json');
			}
		}
		return headers;
	}

	/**
	 * NOTE: This method should be removed when axios properly supports the Headers class.
	 */
	public convertHeadersToRecord(headers: Headers): Record<string, string> {
		// Convert Headers instance to a simple object.
		const headersRecord: Record<string, string> = {};
		headers.forEach((value: string, key: string): void => {
			headersRecord[key] = value;
		});
		return headersRecord;
	}

	private getApiEndpointInformation(
		configuration: ApiEndpointConfiguration<string>
	): Required<IApiEndpointInformation> {
		const information = this.serverApiBuilderService.getApiEndpointInformation(configuration.serverApi);
		// Use the current session grant type, if available, adds supports for ClientCredentials endpoints to work with Password grant types.
		if (this.authTokenService.session?.grantType) {
			information.grantType = this.authTokenService.session.grantType;
		}
		// Allow login requests to override the submitted grant type.
		if (configuration.body instanceof SerializableParams) {
			const formDataGrantType = configuration.body.get('grant_type');
			if (isAuthGrantType(formDataGrantType) && formDataGrantType !== AuthGrantType.RefreshToken) {
				information.grantType = formDataGrantType;
			}
		}
		return information;
	}

	private handleHttpErrorResponse(
		requestConfiguration: ApiEndpointConfiguration<string>,
		axiosError: AxiosError
	): Observable<never> {
		if (this.isNetworkErrorResponse(axiosError) || isNil(axiosError.response)) {
			const networkError = new UnknownNetworkError(HttpStatusText[HttpStatusCode.UNKNOWN_NETWORK_ERROR]);
			return throwError(networkError);
		}
		// Convert `HttpErrorResponse` to `MobizHttpErrorResponse`.
		const mobizError: MobizHttpErrorResponse = this.httpErrorResponseHelper.parseErrorResponseData(
			requestConfiguration.serverApi,
			axiosError.response.status,
			axiosError.response.data
		);
		return throwError(mobizError);
	}

	/**
	 * Handle low level network errors.
	 * @see https://github.com/axios/axios/blob/9a78465a9268dcd360d7663de686709a68560d3d/lib/adapters/xhr.js#L80-L88
	 */
	private isNetworkErrorResponse(error: AxiosError): boolean {
		return error.response === undefined || error.message === 'Network Error';
	}

	private isAxiosError(error: unknown): error is AxiosError {
		return !!get(error, 'isAxiosError');
	}
}
